import { Link } from 'react-router-dom';

/**
 * FunctionalHeroV2
 * 功能型頁面（Cart / Checkout / OrderSuccess / OrderTracking / Payment*）共用的「壓縮版 Hero」。
 *
 * 設計語言：
 * - 高度約 280–320px（不像首頁 Hero 那麼高，避免把表單推太下面）
 * - 深色滿版背景圖 + 黑色 overlay + 底部漸層
 * - 左下角放置 eyebrow（小字）+ 大字標題（EB Garamond + Noto Serif TC）
 * - 標題下方可選顯示麵包屑導引
 * - 字重一律 extralight / light，符合 Barista Map 工業風
 *
 * 與 HeaderV2 配合：本元件不含 spacer，請父層在 Hero 之上自行放置 HeaderV2。
 * HeaderV2 為實心白底黑字（非 transparent variant），所以無需 spacer 也不會遮擋。
 */

export interface FunctionalHeroBreadcrumb {
  label: string;
  to?: string;
}

interface FunctionalHeroV2Props {
  /** 小字眉標，例如「結帳 / Checkout」「訂單 / Order」 */
  eyebrow?: string;
  /** 大字主標題，襯線字體 */
  title: string;
  /** 副標說明文字（可選） */
  subtitle?: string;
  /** 背景圖 URL（深色為佳） */
  backgroundImage: string;
  /** 麵包屑導引（可選） */
  breadcrumbs?: FunctionalHeroBreadcrumb[];
  /** Hero 高度，預設 320px */
  height?: string;
}

export default function FunctionalHeroV2({
  eyebrow,
  title,
  subtitle,
  backgroundImage,
  breadcrumbs,
  height = 'h-[320px] md:h-[360px]',
}: FunctionalHeroV2Props) {
  return (
    <section
      className={`relative w-full ${height} overflow-hidden bg-black`}
      style={{
        fontFamily: "'EB Garamond', 'Noto Serif TC', serif",
      }}
    >
      {/* 背景圖 */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${backgroundImage})` }}
      />
      {/* 黑色 overlay */}
      <div className="absolute inset-0 bg-black/55" />
      {/* 底部漸層強化文字可讀性 */}
      <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />

      {/* 內容容器 */}
      <div className="relative h-full mx-auto max-w-[1600px] px-6 md:px-12 flex flex-col justify-end pb-10 md:pb-14">
        {eyebrow && (
          <div
            className="mb-3 text-[11px] md:text-xs uppercase tracking-[0.32em] text-white/70 font-light"
            style={{ fontFamily: "'EB Garamond', 'Noto Serif TC', serif" }}
          >
            {eyebrow}
          </div>
        )}
        <h1
          className="text-white text-4xl md:text-6xl lg:text-7xl font-extralight leading-[1.05] tracking-tight"
          style={{ fontFamily: "'EB Garamond', 'Noto Serif TC', serif" }}
        >
          {title}
        </h1>
        {subtitle && (
          <p className="mt-4 max-w-2xl text-sm md:text-base text-white/75 font-light leading-relaxed">
            {subtitle}
          </p>
        )}
        {breadcrumbs && breadcrumbs.length > 0 && (
          <nav
            aria-label="Breadcrumb"
            className="mt-6 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] md:text-xs uppercase tracking-[0.24em] text-white/60 font-light"
          >
            {breadcrumbs.map((crumb, idx) => {
              const isLast = idx === breadcrumbs.length - 1;
              return (
                <span key={`${crumb.label}-${idx}`} className="flex items-center gap-2">
                  {crumb.to && !isLast ? (
                    <Link
                      to={crumb.to}
                      className="hover:text-white transition-colors duration-200"
                    >
                      {crumb.label}
                    </Link>
                  ) : (
                    <span className={isLast ? 'text-white/85' : ''}>{crumb.label}</span>
                  )}
                  {!isLast && <span className="text-white/30">/</span>}
                </span>
              );
            })}
          </nav>
        )}
      </div>
    </section>
  );
}
